/* eslint-disable no-console */
import { useEffect, useState } from "react";

import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import moment from "moment";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CardTitle from "components/CardTitle";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context";

import DataTable from "components/DataTable";
import { useSelector, useDispatch, connect } from "react-redux";

import { deleteProjectImage } from "store/actions/mainUserActions";
import {
  getAllServiceCategories,
  updateServiceCategory,
  removeServiceCategories,
} from "store/actions/servicesActions";
import ServiceCategoryForm from "./ServiceCategoryForm";

function CellDate({ value }) {
  return <span>{value ? moment(value).format("DD/MM/YYYY HH:mm") : ""}</span>;
}
CellDate.propTypes = { value: PropTypes.number };
CellDate.defaultProps = { value: 0 };

function AlertDialog({ open, handleClose, handleAgree }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={handleClose}>
      <MDBox
        sx={({ palette: { white, background } }) => ({
          backgroundColor: darkMode ? background.sidenav : white.main,
        })}
      >
        <DialogTitle>{t("confirmation")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("stepNotUndo")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose}>{t("cancel")}</MDButton>
          <MDButton onClick={handleAgree} autoFocus>
            {t("OK")}
          </MDButton>
        </DialogActions>
      </MDBox>
    </Dialog>
  );
}

AlertDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleAgree: PropTypes.func,
};
AlertDialog.defaultProps = {
  handleAgree: () => {},
};

function ServiceCategories({
  serviceCategories,
  actGetServicesCategories,
  actUpdateServiceCategory,
  actRemoveServiceCategories,
  mainUserId,
  projectId,
}) {
  const [open, setOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [uids, setUids] = useState([]);
  const [actionType, setActionType] = useState("");

  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    actGetServicesCategories();
  }, [mainUserId, actGetServicesCategories]);

  const columns = () => [
    { Header: t("name"), accessor: "name" },
    { Header: t("createdAt"), accessor: "createdAt", Cell: CellDate },
  ];

  const remove = (data) => {
    setUids(data.map((d) => d.original));
    setActionType("remove");
    setOpenConfirm(true);
  };

  const onSubmit = (data) => {
    if (actionType === "edit" || actionType === "add")
      actUpdateServiceCategory({
        mainUserId,
        projectId,
        serviceCategoryData: data,
      });

    setOpen(false);
  };

  const deleteImagePromise = (imageUid) =>
    new Promise((resolve) => {
      dispatch(
        deleteProjectImage({
          imageUid,
          projectId,
          cb: () => resolve(true),
        })
      );
    });

  const confirm = async () => {
    if (actionType !== "remove") return;

    const selected = Array.isArray(uids) ? uids : [];
    const imageUids = selected.map((c) => c?.imageUid).filter(Boolean);

    await Promise.all(imageUids.map(deleteImagePromise));

    actRemoveServiceCategories({
      mainUserId,
      projectId,
      servicesCategories: selected,
    });

    setOpenConfirm(false);
    setUids([]);
    setActionType("");
  };

  const add = () => {
    setActionType("add");
    setUids([]);
    setOpen(true);
  };

  const edit = (indexes) => {
    const ids = indexes.map((i) => i.original.uid);
    setUids(ids);
    setActionType("edit");
    setOpen(true);
  };

  const menuConfig = useSelector((state) => state.mainUserReducer.customApp.menuConfig);
  const categoriesLabel = t(
    menuConfig?.servicesCategories?.label ??
      menuConfig?.servicesCategories?.defaultLabel ??
      "servicesCategories"
  );

  const content = (
    <>
      <AlertDialog
        open={openConfirm}
        handleClose={() => setOpenConfirm(false)}
        handleAgree={confirm}
      />

      <Modal open={open} onClose={() => setOpen(false)} style={{ overflow: "scroll" }}>
        <MDBox
          mt={3}
          sx={{
            width: { xs: "90vw", md: "65vw" },
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <ServiceCategoryForm
            onSubmit={onSubmit}
            actionType={actionType}
            uids={uids}
            projectId={projectId}
          />
        </MDBox>
      </Modal>

      <CardTitle title={categoriesLabel} icon="category" bgColor="warning" />

      <DataTable
        table={{ columns: columns(), rows: Object.values(serviceCategories || {}) }}
        canSearch
        canSelect
        canEdit
        canAdd
        canActuate={false}
        add={add}
        edit={edit}
        remove={remove}
      />
    </>
  );

  return <Card>{content}</Card>;
}

ServiceCategories.propTypes = {
  serviceCategories: PropTypes.object,
  projectId: PropTypes.string,
  actGetServicesCategories: PropTypes.func.isRequired,
  actUpdateServiceCategory: PropTypes.func.isRequired,
  actRemoveServiceCategories: PropTypes.func.isRequired,
  mainUserId: PropTypes.string.isRequired,
};

ServiceCategories.defaultProps = {
  serviceCategories: {},
  projectId: "",
};

const mapStateToProps = (state, ownProps) => {
  const { servicesReducer, projectsReducer } = state;
  const { projectId } = ownProps;

  const allServicesCategories = servicesReducer?.servicesCategories || {};
  const project = projectsReducer ? projectsReducer[projectId] : {};
  const servicesCategoriesIds = project?.servicesCategories || {};

  const projectServicesCategories = {};

  Object.keys(servicesCategoriesIds).forEach((id) => {
    if (allServicesCategories[id]) {
      projectServicesCategories[id] = allServicesCategories[id];
    }
  });

  return { serviceCategories: projectServicesCategories };
};

const mapDispatchToProps = (dispatch) => ({
  actGetServicesCategories: () => dispatch(getAllServiceCategories()),
  actUpdateServiceCategory: (data) => dispatch(updateServiceCategory(data)),
  actRemoveServiceCategories: (data) => dispatch(removeServiceCategories(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ServiceCategories);
